import curry from 'lodash/fp/curry';
import flow from 'lodash/fp/flow';

// https://regex101.com/r/ocVvOb/1
export const CAPTURING_DATA_OUTLOOK_TRACE_RE =
  /(<img.*?)(data-outlook-trace=".*?" |src=".*?" )(data-outlook-trace=".*?"|src=".*?")(.*?>)/g;

// https://regex101.com/r/BFOSPI/1
export const CAPTURING_DATA_GMAIL_ALT_RE =
  /(<img.*?)(alt=".*?" |src=".*?" )(alt=".*?"|src=".*?")(.*?>)/g;

const IMAGE_CONTENT_TYPES = {
  'image/png': true,
  'image/jpeg': true,
  'image/jpg': true,
  'image/gif': true,
  'image/webp': true,
};

/**
 * Replaces the '<img>' strings in the body param with one that has a 'src'
 * attribute that matches with an attachment.
 *
 * Emails from Gmail have an "alt" attribute that matches up with the
 * "fileName" key in one of the attachments` items.
 *
 * Outlook has nothing that explicitly says "this image matches up with this
 * attachment", other than a 'contenttype="image/png|jpeg|gif"' attribute.
 * Using that, we find the first attachment that has a matching 'contentType'
 * value, and use the 's3Url' from that attachment for the <img src>.
 *
 * Once an attachment has been matched with an <img>, we remove that attachment
 * from the attachments array. This approach means we have to mutate the array,
 * removing the matched image attachments as we go. This allows for the
 * subsequent <img>s to be populated with other s3Urls from attachments.
 * The returned array is one that has all of the matched attachments removed.
 * This returned array can be used to show the attachments UI that represents
 * all non-inline image attachments.
 *
 * The first 3 arguments are meant to be "curry-ed", allowing for specific
 * versions of this function to be created for each client.
 *
 * @param {RegExp} imageRE finds <img>s in the body
 * @param {Array} params from the email; a tuple so it works better with flow
 * @param {Array[0]} email body
 * @param {Array[1]} email attachments
 */
export function replaceInlineImage(imageRE, [body, attachments = []]) {
  if (!attachments.length) {
    return [body, attachments];
  }

  // Get all of the <img>s from the body text
  const imgMatches = body.matchAll(imageRE);
  let updatedBody = body;

  // Iterate over all of the <img> matches.
  Array.from(imgMatches).forEach((matchArr) => {
    if (attachments.length) {
      for (let i = 0; i < attachments.length; i += 1) {
        // Find the first attachment with the same value and use that
        // to find the corresponding attachment.
        if (IMAGE_CONTENT_TYPES[attachments[i].contentType]) {
          // Remove the attachment from the attachments array, marking this
          // attachment as "spoken for" so to speak.
          const [attachment] = attachments.splice(i, 1);
          const srcMatch = matchArr.find(
            (match) => match.substr(0, 3) === 'src',
          );

          if (srcMatch) {
            updatedBody = updatedBody.replace(
              srcMatch.trim(),
              `src="${attachment.s3Url}"`,
            );
          }
          break;
        }
      }
    }
  });

  return [updatedBody, attachments];
}

/**
 * @param {Array}
 * @param {Array[0]} email body
 * @param {Array[1]} email attachments
 */
export const replaceGmailInlineImage = curry(replaceInlineImage)(
  CAPTURING_DATA_GMAIL_ALT_RE,
);

/**
 * @param {Array}
 * @param {Array[0]} email body
 * @param {Array[1]} email attachments
 */
export const replaceOutlookInlineImage = curry(replaceInlineImage)(
  CAPTURING_DATA_OUTLOOK_TRACE_RE,
);

/**
 * @param {Array}
 * @param {Array[0]} email body
 * @param {Array[1]} email attachments
 */
export const replaceInlineImages = flow(
  replaceGmailInlineImage,
  replaceOutlookInlineImage,
);
